// src/utils/exportReport.js

/**
 * Export resume analysis results as a formatted HTML report
 */

export function exportAsHTML(results, resumeName = 'Resume', jobTitle = 'Position') {
  const atsScore = results.atsScore?.overall || results.scores?.overall || 0;
  const matchScore = results.keywordAnalysis?.relevanceScore || results.scores?.keywordMatch || 0;
  const matchedKeywords = results.keywordAnalysis?.matched || results.keywords?.matched || [];
  const missingKeywords = (results.keywordAnalysis?.missing || results.keywords?.missing || []).slice(0, 20);
  const strengths = results.strengths || [];
  const weaknesses = results.weaknesses || [];
  const recommendations = results.recommendations || [];
  
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Resume Analysis Report - ${resumeName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
      line-height: 1.6; color: #1f2937; background: #f9fafb; padding: 40px 20px;
    }
    .container {
      max-width: 900px; margin: 0 auto; background: white; padding: 60px;
      border-radius: 12px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
    .header {
      text-align: center; border-bottom: 3px solid #0891b2;
      padding-bottom: 30px; margin-bottom: 40px;
    }
    .header h1 { font-size: 32px; color: #0891b2; margin-bottom: 10px; }
    .header .subtitle { font-size: 18px; color: #6b7280; }
    .header .date { font-size: 14px; color: #9ca3af; margin-top: 10px; }
    .score-grid {
      display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 20px; margin-bottom: 40px;
    }
    .score-card {
      background: #f0fdfa; border: 2px solid #5eead4;
      border-radius: 12px; padding: 30px; text-align: center;
    }
    .score-value { font-size: 48px; font-weight: bold; color: #0891b2; margin-bottom: 10px; }
    .score-label { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 5px; }
    .score-desc { font-size: 14px; color: #6b7280; }
    .section { margin-bottom: 40px; }
    .section-title {
      font-size: 24px; font-weight: bold; color: #111827; margin-bottom: 20px;
      padding-bottom: 10px; border-bottom: 2px solid #e5e7eb;
    }
    .keywords { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 30px; }
    .keyword-matched {
      background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7;
      padding: 8px 16px; border-radius: 8px; font-size: 14px; font-weight: 500;
    }
    .keyword-missing {
      background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5;
      padding: 8px 16px; border-radius: 8px; font-size: 14px; font-weight: 500;
    }
    .list-item {
      background: #f9fafb; border-left: 4px solid #0891b2;
      padding: 15px 20px; margin-bottom: 15px; border-radius: 6px;
    }
    .list-item-title { font-weight: 600; color: #111827; margin-bottom: 5px; }
    .list-item-desc { color: #4b5563; font-size: 14px; }
    .strength-item { border-left-color: #10b981; background: #f0fdf4; }
    .weakness-item { border-left-color: #f59e0b; background: #fffbeb; }
    .footer {
      margin-top: 60px; padding-top: 30px; border-top: 2px solid #e5e7eb;
      text-align: center; color: #6b7280; font-size: 14px;
    }
    .footer-brand { font-weight: 600; color: #0891b2; }
    @media print {
      body { background: white; padding: 0; }
      .container { box-shadow: none; padding: 40px; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Resume Analysis Report</h1>
      <div class="subtitle">${resumeName} - ${jobTitle}</div>
      <div class="date">Generated on ${date}</div>
    </div>

    <div class="score-grid">
      <div class="score-card">
        <div class="score-value">${atsScore}%</div>
        <div class="score-label">ATS Score</div>
        <div class="score-desc">Resume compatibility</div>
      </div>
      <div class="score-card">
        <div class="score-value">${matchScore}%</div>
        <div class="score-label">Match Score</div>
        <div class="score-desc">Job alignment</div>
      </div>
      <div class="score-card">
        <div class="score-value">${matchedKeywords.length}</div>
        <div class="score-label">Keywords Found</div>
        <div class="score-desc">Critical terms matched</div>
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">✓ Matched Keywords</h2>
      <div class="keywords">
        ${matchedKeywords.slice(0, 30).map(kw => 
          `<span class="keyword-matched">${typeof kw === 'string' ? kw : kw.term || kw}</span>`
        ).join('')}
      </div>
    </div>

    <div class="section">
      <h2 class="section-title">⚠ Missing Keywords</h2>
      <div class="keywords">
        ${missingKeywords.map(kw => 
          `<span class="keyword-missing">${kw}</span>`
        ).join('')}
      </div>
    </div>

    ${strengths.length > 0 ? `
    <div class="section">
      <h2 class="section-title">💪 Strengths</h2>
      ${strengths.map(strength => 
        `<div class="list-item strength-item"><div class="list-item-desc">${strength}</div></div>`
      ).join('')}
    </div>
    ` : ''}

    ${weaknesses.length > 0 ? `
    <div class="section">
      <h2 class="section-title">📈 Areas for Improvement</h2>
      ${weaknesses.map(weakness => 
        `<div class="list-item weakness-item"><div class="list-item-desc">${weakness}</div></div>`
      ).join('')}
    </div>
    ` : ''}

    ${recommendations.length > 0 ? `
    <div class="section">
      <h2 class="section-title">💡 Recommendations</h2>
      ${recommendations.map(rec => 
        `<div class="list-item">
          <div class="list-item-title">${rec.title || rec}</div>
          ${rec.description ? `<div class="list-item-desc">${rec.description}</div>` : ''}
        </div>`
      ).join('')}
    </div>
    ` : ''}

    <div class="footer">
      <div>Generated by <span class="footer-brand">SmartResumeTool</span></div>
      <div style="margin-top: 5px;">AI-Powered Resume Analysis</div>
    </div>
  </div>
</body>
</html>
  `;

  return html;
}

export function downloadReport(results, resumeName = 'Resume', jobTitle = 'Position') {
  const html = exportAsHTML(results, resumeName, jobTitle);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Resume_Analysis_Report_${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default { exportAsHTML, downloadReport };